import {ArrowDown, ArrowUp, ArrowUpDown, Eye, FileSearch, UserRound} from 'lucide-react'
import type {ReactNode} from 'react'
import type {LogRecord} from './types'
import {formatLogDate} from './logUtils'

interface LogTableProps {
  data: LogRecord[]
  onViewDetails: (item: LogRecord) => void
  onUserSelect: (userId: number | null) => void
  sortField: string
  sortDirection: 'asc' | 'desc'
  onSort: (field: string) => void
}

export const getLogTypeColor = (type: string): string => {
  const value = (type || '').toLowerCase()
  if (/erro|error|fail|falha/.test(value)) return 'danger'
  if (/sucesso|success/.test(value)) return 'success'
  if (/aviso|warning|alert/.test(value)) return 'warning'
  if (/info/.test(value)) return 'info'
  if (/briefing|search|busca/.test(value)) return 'primary'
  return 'neutral'
}

export const LogTypeBadge = ({type}: {type: string}) => (
  <span className={`logs-type-badge logs-type-badge--${getLogTypeColor(type)}`}>
    <span className='logs-type-badge-dot' aria-hidden='true' />
    <span className='logs-type-badge-label' title={type || 'Sem tipo'}>
      {type || 'Sem tipo'}
    </span>
  </span>
)

export const LogUser = ({
  item,
  onUserSelect,
}: {
  item: Pick<LogRecord, 'user' | 'user_id'>
  onUserSelect: (userId: number | null) => void
}) => {
  const name = item.user?.trim() || 'Sistema / não identificado'
  const words = name.split(/\s+/)
  const initials = [words[0], words.length > 1 ? words[words.length - 1] : '']
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
  const content = (
    <>
      <span className='logs-avatar' aria-hidden='true'>
        {item.user ? initials : <UserRound size={17} />}
      </span>
      <span className='logs-user-text'>
        <span className='logs-user-name' title={name}>
          {name}
        </span>
        <span className='logs-user-caption'>{'Ver relatório de atividade'}</span>
      </span>
    </>
  )

  return (
    <button
      type='button'
      className='logs-user logs-user-button'
      onClick={() => onUserSelect(item.user_id)}
      aria-label={`Ver relatório de ${name}`}
    >
      {content}
    </button>
  )
}

export const LogEmptyState = () => (
  <div className='logs-empty' role='status'>
    <span className='logs-empty-icon'>
      <FileSearch size={30} aria-hidden='true' />
    </span>
    <strong>Nenhum registro encontrado</strong>
    <p>Ajuste os filtros ou amplie o período para encontrar outras atividades.</p>
  </div>
)

const LogTable = ({
  data,
  onViewDetails,
  onUserSelect,
  sortField,
  sortDirection,
  onSort,
}: LogTableProps) => {
  const sortableHeader = (field: string, label: ReactNode, className = '') => {
    const active = sortField === field
    const Icon = active ? (sortDirection === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown
    return (
      <th
        scope='col'
        className={className}
        aria-sort={active ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type='button'
          className={`logs-sort-button${active ? ' is-active' : ''}`}
          onClick={() => onSort(field)}
          aria-label={`Ordenar por ${label} em ordem ${
            active && sortDirection === 'desc' ? 'crescente' : 'decrescente'
          }`}
        >
          {label}
          <Icon size={14} aria-hidden='true' />
        </button>
      </th>
    )
  }

  return (
    <div className='logs-table-wrap'>
      <table className='logs-table'>
        <caption className='visually-hidden'>
          Registros de atividade do sistema. Selecione um usuário para ver seu relatório.
        </caption>
        <colgroup>
          <col className='logs-table-col-user' />
          <col className='logs-table-col-activity' />
          <col className='logs-table-col-type' />
          <col className='logs-table-col-date' />
          <col className='logs-table-col-action' />
        </colgroup>
        <thead>
          <tr>
            {sortableHeader('user', 'Usuário')}
            {sortableHeader('name', 'Atividade')}
            {sortableHeader('type', 'Tipo')}
            {sortableHeader('created_at', 'Data e hora', 'logs-date-heading')}
            <th scope='col' className='logs-action-cell'>
              <span className='visually-hidden'>Ações</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={5}>
                <LogEmptyState />
              </td>
            </tr>
          ) : (
            data.map((item) => {
              const formattedDate = formatLogDate(item.created_at)
              const [date, time] = formattedDate.split(' ')
              return (
                <tr key={item.id_log}>
                  <td>
                    <LogUser item={item} onUserSelect={onUserSelect} />
                  </td>
                  <td>
                    <div className='logs-description'>{item.name || 'Atividade sem descrição'}</div>
                    <span className='logs-record-id'>#{item.id_log}</span>
                  </td>
                  <td>
                    <LogTypeBadge type={item.type} />
                  </td>
                  <td className='logs-date-cell'>
                    <span className='logs-date' aria-label={formattedDate}>
                      <span className='logs-date-day'>{date}</span>
                      {time && <span className='logs-date-time'>{time}</span>}
                    </span>
                  </td>
                  <td className='logs-action-cell'>
                    <button
                      type='button'
                      className='logs-details-button'
                      onClick={() => onViewDetails(item)}
                      aria-label={`Ver detalhes do registro ${item.id_log}`}
                    >
                      <Eye size={16} aria-hidden='true' />
                      <span>Detalhes</span>
                    </button>
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}

export default LogTable
